import { Client, IMessage, StompSubscription, ReconnectionTimeMode } from '@stomp/stompjs'
import { BASE_URL, WS_TYPE } from '@/config/constant'

// 订阅目标
const SUBSCRIBE_DESTINATION = '/exchange/platform/#'
// 重连基础间隔与上限（指数退避）
const RECONNECT_DELAY = 5000
const MAX_RECONNECT_DELAY = 30000
// 心跳间隔
const HEARTBEAT = 10000

export type SocketMessageHandler = (body: any) => void
export type SocketStatusHandler = (connected: boolean) => void

interface ConnectOptions {
	// 收到业务消息时回调（已解析为对象）
	onMessage: SocketMessageHandler
	// 连接状态变化回调
	onStatusChange?: SocketStatusHandler
}

// 单例连接与订阅，全部由本模块统一管理
let client: Client | null = null
let subscription: StompSubscription | null = null

/**
 * 建立（或复用）WebSocket 连接。
 * 断线重连、重连后自动重新订阅均由 @stomp/stompjs 内置机制处理，
 * 无需在外部维护定时器或递归重连逻辑。
 */
export function connectSocket({ onMessage, onStatusChange }: ConnectOptions): Client {
	// 已存在活跃连接则直接复用，避免重复建连互相顶掉
	if (client?.active) {
		return client
	}

	// 清理可能残留的旧实例
	disconnectSocket()

	client = new Client({
		brokerURL: `${WS_TYPE}://${BASE_URL}/ws`,
		connectHeaders: {
			login: 'admin',
			passcode: 'qwer1234'
		},
		debug: () => { }, // 关闭调试日志
		reconnectDelay: RECONNECT_DELAY, // 内置断线重连（含首次连接失败重试）
		reconnectTimeMode: ReconnectionTimeMode.EXPONENTIAL, // 指数退避，缓解服务端压力
		maxReconnectDelay: MAX_RECONNECT_DELAY, // 重连间隔上限
		heartbeatIncoming: HEARTBEAT, // 期望服务端心跳，用于探测半开连接
		heartbeatOutgoing: HEARTBEAT // 客户端主动发心跳维持连接
	})

	// 每次（重）连接成功都会触发；订阅放在这里以保证重连后自动恢复
	client.onConnect = () => {
		console.log('WebSocket 已连接')
		onStatusChange?.(true)
		subscription = client!.subscribe(SUBSCRIBE_DESTINATION, (message: IMessage) => {
			let body: any
			try {
				body = JSON.parse(message.body)
			} catch (e) {
				console.error('WebSocket 消息解析失败:', e)
				return
			}
			onMessage(body)
		})
	}

	// 连接断开 / 底层 WebSocket 关闭时更新状态（stompjs 会随后自动重连）
	client.onDisconnect = () => onStatusChange?.(false)
	client.onWebSocketClose = () => onStatusChange?.(false)

	// 错误回调
	client.onStompError = (frame) => {
		console.error('STOMP 错误:', frame.headers['message'], frame.body)
	}
	client.onWebSocketError = (error) => {
		console.error('WebSocket 错误:', error)
	}

	client.activate()
	return client
}

// 断开 socket 连接（deactivate 会同时停止内置重连）
export function disconnectSocket(): void {
	try {
		subscription?.unsubscribe()
	} catch (e) { }
	subscription = null

	if (client) {
		client.deactivate()
		client = null
		console.log('WebSocket 已断开')
	}
}

// 当前是否已连接
export function isSocketConnected(): boolean {
	return !!client?.connected
}
